import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import enTranslations from '../translations/en.json';

const BASE_URL = process.env.REACT_APP_PUBLIC_TRANSLATION_BASE_URL;

const mergeTranslations = (base, override) => {
	if (!override) return base;

	const merged = { ...base };
	Object.keys(override).forEach((key) => {
		const baseValue = base?.[key];
		const overrideValue = override[key];

		if (
			overrideValue &&
			typeof overrideValue === 'object' &&
			!Array.isArray(overrideValue) &&
			baseValue &&
			typeof baseValue === 'object' &&
			!Array.isArray(baseValue)
		) {
			merged[key] = mergeTranslations(baseValue, overrideValue);
		} else {
			merged[key] = overrideValue;
		}
	});

	return merged;
};

const loadLocalTranslations = async (language) => {
	try {
		const localResponse = await import(`../translations/${language}.json`);
		return localResponse.default;
	} catch {
		return enTranslations;
	}
};

export const loadTranslations = createAsyncThunk(
	'translation/loadTranslations',
	async (language, { rejectWithValue }) => {
		try {
			const localTranslations = await loadLocalTranslations(language);
			const baseTranslations = mergeTranslations(enTranslations, localTranslations);

			// Check alternativeText is present then load entitySpecific translations
			const hasAlternativeText = Boolean(global.alternativeText);
			let url;
			if (hasAlternativeText && global.entityId) {
				url = `${BASE_URL}/${global.entityId}/${language}.json`;
			} else {
				url = `${BASE_URL}/default/${language}.json`;
			}
			const response = await fetch(url);
			if (response.status === 200) {
				const data = await response.json();
				return {
					translations: mergeTranslations(baseTranslations, data),
					language,
				};
			}
			return { translations: baseTranslations, language };
		} catch (error) {
			return rejectWithValue({ translations: enTranslations, language: 'en' });
		}
	}
);

const translationSlice = createSlice({
	name: 'translation',
	initialState: {
		translations: enTranslations,
		currentLanguage: 'en',
	},
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(loadTranslations.fulfilled, (state, action) => {
				state.translations = action.payload.translations;
				state.currentLanguage = action.payload.language;
			})
			.addCase(loadTranslations.rejected, (state, action) => {
				state.translations = action.payload.translations;
				state.currentLanguage = action.payload.language;
			});
	},
});

export default translationSlice.reducer;
